import type { KeyboardEvent, Ref } from 'react'
import { cn } from '@klear/ui/lib/utils'

export type ChoiceGroupProps<T> = {
  label: string
  choices: readonly { value: T; label: string }[]
  value: T
  disabled: boolean
  onChange: (value: T) => void
  selectedRef?: Ref<HTMLButtonElement>
}

export function ChoiceGroup<T>({ label, choices, value, disabled, onChange, selectedRef }: ChoiceGroupProps<T>) {
  const at = choices.findIndex((choice) => choice.value === value)
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
    event.preventDefault()
    const step = event.key === 'ArrowRight' ? 1 : choices.length - 1
    const index = (at + step) % choices.length
    const next = choices[index]
    if (next === undefined) return
    onChange(next.value)
    event.currentTarget.querySelectorAll('button')[index]?.focus()
  }
  return (
    <div
      role="radiogroup"
      aria-label={label}
      onKeyDown={onKeyDown}
      className="bg-muted inline-flex rounded-md border p-0.5"
    >
      {choices.map((choice) => {
        const on = value === choice.value
        const reachable = on || (at === -1 && choice === choices[0])
        return (
          <button
            key={choice.label}
            ref={on ? selectedRef : undefined}
            type="button"
            role="radio"
            aria-checked={on}
            tabIndex={reachable ? 0 : -1}
            disabled={disabled}
            onClick={() => onChange(choice.value)}
            className={cn(
              'focus-visible:ring-ring rounded px-2.5 py-0.5 text-sm outline-none focus-visible:ring-2',
              on ? 'bg-background text-foreground font-medium shadow-sm' : 'text-muted-foreground hover:text-foreground'
            )}
          >
            {choice.label}
          </button>
        )
      })}
    </div>
  )
}
